import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { cloneDeep } from 'lodash';
import {
  emptyFatca,
  TAdditionalInformationFormValues,
} from '~/modules/identification/forms/AdditionalInformation/types.ts';
import {
  fatcaValidationSchema,
  pepValidationSchema,
} from '~/modules/identification/forms/AdditionalInformation/validationSchema.ts';
import { useMemo } from 'react';
import {
  emptyPep,
  FATCA_TIN,
} from '~/modules/identification/forms/AdditionalInformation/constants.ts';
import { useFeatureFlags } from '~/helpers/FeatureFlagsContext';
import useGetContragentTypes from '~/modules/identification/hooks/useGetContragentTypes.ts';
import {
  selectGeneralInfoSoleTrader,
  selectPepInStore,
} from '~/store/identification-form/selectors';
import { useUSACountryFlags } from '~/modules/identification/forms/GeneralInfo/hooks/useUSACountryFlags.ts';

export const useAdditionalInformationForm = () => {
  const pepInStore = useSelector(selectPepInStore);
  const { isSoleTrader } = useGetContragentTypes();
  const { fullNameInEng } = useSelector(selectGeneralInfoSoleTrader);
  const { isCRSIFopEnabled, isFATCAFopEnabled } = useFeatureFlags();
  const { birthplaceCountryUSA, citizenshipCountryUSA, permanentAddressUSA } = useUSACountryFlags();

  const isUSA = birthplaceCountryUSA || citizenshipCountryUSA || permanentAddressUSA;
  const isNativeCountryUSA = permanentAddressUSA || birthplaceCountryUSA;

  const initialValues = useMemo<TAdditionalInformationFormValues>(() => {
    const fatca = pepInStore?.fatca ?? emptyFatca;

    return {
      additionalInformation: pepInStore?.additionalInformation?.length
        ? pepInStore.additionalInformation
        : [emptyPep],
      fatca: {
        ...fatca,
        fullNameInEng: fatca.fullNameInEng ?? fullNameInEng,
        tinType: fatca.tinType ?? FATCA_TIN.SSN,
      },
    };
  }, [pepInStore?.additionalInformation, pepInStore?.fatca, fullNameInEng]);

  const schema = yup.object({
    additionalInformation: pepValidationSchema(isCRSIFopEnabled, isSoleTrader),
    fatca: fatcaValidationSchema(
      isSoleTrader,
      isFATCAFopEnabled,
      isUSA,
      isNativeCountryUSA,
      birthplaceCountryUSA,
      permanentAddressUSA
    ),
  });

  const form = useForm<TAdditionalInformationFormValues>({
    resolver: yupResolver(schema) as unknown as Resolver<TAdditionalInformationFormValues>,
    mode: 'onTouched',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    defaultValues: cloneDeep(initialValues),
  });

  return form;
};
